import { useEffect, useState } from 'react';
import { trackingApi } from '../api/services';
import toast from 'react-hot-toast';
import { Download, FileDown, Info } from 'lucide-react';

export default function MboExport() {
  const [preview, setPreview] = useState('');
  const [forceAll, setForceAll] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await trackingApi.mboExportPreview(forceAll);
      setPreview(res.data || '');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load preview');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [forceAll]);

  const lineCount = preview ? Math.max(0, preview.trim().split('\n').length - 1) : 0;

  function download() {
    const blob = new Blob([preview], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mbo-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">MBO tracking export</h1>
      <p className="text-sm text-gray-500 mb-5">
        Download a CSV matching MBO's expected format (1st TN per order = <em>Dispatch</em>,
        subsequent TNs = <em>In Transit</em>). You upload this to the MBO portal manually —
        nothing is pushed automatically.
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-start gap-2">
        <Info className="w-4 h-4 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-900">
          Only shipments with a real tracking number (not pending, not cancel)
          and not yet marked as exported are included. Use "Force all"
          to re-include already-exported rows.
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={forceAll} onChange={(e) => setForceAll(e.target.checked)} />
          Force all (include already-exported)
        </label>
        <button onClick={load} className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          Refresh
        </button>
        <button
          onClick={download} disabled={lineCount === 0}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> Download CSV ({lineCount} rows)
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-200 text-xs font-semibold uppercase text-gray-500 flex items-center gap-2">
          <FileDown className="w-3 h-3" /> Preview
        </div>
        {loading && <div className="p-4 text-sm text-gray-500">Loading…</div>}
        {!loading && lineCount === 0 && (
          <div className="p-4 text-sm text-gray-500">
            Nothing to export right now. Upload tracking first, or tick "Force all" to re-export.
          </div>
        )}
        {!loading && lineCount > 0 && (
          <pre className="p-4 text-xs font-mono whitespace-pre-wrap max-h-[60vh] overflow-auto">
{preview}
          </pre>
        )}
      </div>
    </div>
  );
}
